import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import { GiftProduct, GiftProductType } from '../../../../models';
import { ROLETA_RESOURCE, RoletaStatus } from '../../../../models/roleta';

interface Props {
  product: Partial<GiftProduct>;
  onChange: (product: Partial<GiftProduct>) => void;
}

const ROLETA_COLOR = '#7869DF';

const SpinTicketProductForm: React.FC<Props> = ({ product, onChange }) => {
  const dataProvider = useDataProvider();
  const [roletas, setRoletas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Só roleta em rascunho ou no ar pode receber recompensa nova. Roleta
    // inativa ou encerrada não concede giro, e vincular ali criaria um brinde
    // que nunca entrega nada.
    dataProvider.getList(ROLETA_RESOURCE, {
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 50 },
      filter: { status: RoletaStatus.linkableStatuses },
    })
      .then(({ data }) => setRoletas(data))
      .catch(() => setRoletas([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoletaSelect = (roletaId: string) => {
    const selected = roletas.find((roleta) => roleta.id === roletaId);
    onChange({
      ...product,
      type: GiftProductType.SpinTicket,
      roletaId: selected?.id ?? null,
      roletaName: selected?.name ?? null,
      // A quantidade é o multiplicador da conquista: 1 conquista = N giros.
      spinAmount: product.spinAmount || 1,
    });
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Esta recompensa dá ao cliente <strong>giros na Roleta da Sorte</strong> que você já cadastrou.
        Cada vez que o cliente cumprir a regra deste brinde, ele recebe a quantidade de giros configurada aqui —
        não um cupom. O prêmio de verdade sai quando ele girar.
      </Alert>

      {!loading && roletas.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Nenhuma roleta disponível. Só aparecem aqui as roletas em <strong>rascunho</strong> ou <strong>ativas</strong>.
          Cadastre a roleta primeiro em Clube de Vantagens → Roletas.
        </Alert>
      )}

      <TextField
        select
        fullWidth
        required
        label="Roleta vinculada"
        value={product.roletaId || ''}
        onChange={(event) => handleRoletaSelect(event.target.value)}
        disabled={loading || roletas.length === 0}
        helperText="Apenas roletas em rascunho ou ativas podem receber novos giros."
      >
        {roletas.map((roleta) => (
          <MenuItem key={roleta.id} value={roleta.id}>
            {roleta.name}
          </MenuItem>
        ))}
      </TextField>

      <Box mt={3}>
        <TextField
          fullWidth
          required
          type="number"
          label="Giros por conquista"
          value={product.spinAmount ?? 1}
          onChange={(event) => onChange({
            ...product,
            type: GiftProductType.SpinTicket,
            spinAmount: Math.max(1, parseInt(event.target.value, 10) || 1),
          })}
          inputProps={{ min: 1, step: 1 }}
          helperText="Mínimo 1. É a quantidade concedida a cada conquista do brinde."
        />
      </Box>

      {product.roletaId && (
        <Box mt={3} p={2} border="1px solid" borderColor="divider" borderRadius={2}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Roleta selecionada:
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <CasinoIcon sx={{ fontSize: 32, color: ROLETA_COLOR }} />
            <Typography variant="h6">{product.roletaName || product.roletaId}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SpinTicketProductForm;
